"use client";

import { useState } from "react";
import {
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  CircularInputs,
  CircularResults,
  type CircularState,
} from "@/components/tools/physics-mechanics/circular-panel";
import {
  RelationInputs,
  RelationResults,
} from "@/components/tools/physics-mechanics/relation-panel";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  CENTRIPETAL_ANGULAR,
  CENTRIPETAL_SPEED,
  type CentripetalAngularKey,
  type CentripetalSpeedKey,
} from "@/lib/tool-kit/physics-mechanics/circular";
import {
  formatSig,
  SIG_FIG_CHOICES,
  withUnit,
} from "@/lib/tool-kit/physics-mechanics/format";
import {
  evaluateRelation,
  initialRelationState,
} from "@/lib/tool-kit/physics-mechanics/relation";
import { STANDARD_GRAVITY } from "@/lib/tool-kit/physics-mechanics/units";

/** English strings. A translated content file overrides these through `ui`. */
export const CENTRIPETAL_FORCE_CALCULATOR_STRINGS = {
  modeLabel: "What do you know about the motion?",
  modeSpeed: "The speed (Fc = mv²/r)",
  modeAngular: "The angular speed or rpm (Fc = mω²r)",
  modeConvert: "Convert speed, angular speed, period and frequency",
  speedHint:
    "The centripetal force is the net force pointing toward the centre of the circle — tension, friction, gravity or the normal force, whichever supplies it.",
  angularHint:
    "Use this form when you know how fast something spins: ω in rad/s, revolutions per second or rpm.",
  convertHint:
    "Enter any one way of describing the motion and get the rest. A radius adds the linear speed and the centripetal acceleration.",
  solveFor: "Solve for",
  unitFor: "Unit for {name}",
  calculated: "Calculated",
  rounding: "Round answers to",
  sigFigs: "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  force: "Centripetal force Fc",
  mass: "Mass m",
  speed: "Speed v",
  angular: "Angular speed ω",
  radius: "Radius r",
  radiusHint: "Measured from the centre of the circle to the object.",
  workingHeading: "The working",
  stepEquation: "Start from {equation}.",
  stepRearrange: "Rearrange for {symbol}: {form}.",
  stepConvert: "Convert to SI units: {list}.",
  stepSubstitute: "{symbol} = {working} = {value}",
  stepAnswerUnit: "In the unit you chose: {si} = {shown}.",
  copy: "Copy result",
  copied: "Copied!",
  enterAll: "Enter every value except the one you are solving for.",
  unreadable:
    "{name} is not a number. Use digits, a decimal point and an optional minus sign, or scientific notation such as 1.5e3.",
  errors: {
    "mass-not-positive": "Mass must be greater than zero.",
    "radius-not-positive": "The radius must be greater than zero.",
    "force-negative":
      "The centripetal force cannot be negative: it points toward the centre.",
    "speed-zero":
      "At zero speed no centripetal force is needed, so the mass cannot be found. Enter a speed above zero.",
    "angular-speed-zero":
      "At zero angular speed no centripetal force is needed, so this value cannot be found. Enter an angular speed above zero.",
    "force-zero":
      "With no centripetal force the object would move in a straight line, so the radius cannot be found. Enter a force above zero.",
    "not-finite":
      "The result is too large to calculate. Check the numbers and units.",
  },
  extrasHeading: "More about the motion",
  acceleration: "Centripetal acceleration",
  accelerationG: "Centripetal acceleration in g",
  period: "Period T (time for one lap)",
  frequency: "Frequency f",
  omega: "Angular speed ω",
  rpm: "Revolutions per minute",
  muNeeded: "Friction coefficient needed on a flat road",
  givenLabel: "I know the",
  givenSpeed: "Speed v",
  givenOmega: "Angular speed ω",
  givenPeriod: "Period T",
  givenFrequency: "Frequency f",
  radiusOptional: "Radius r (optional)",
  radiusOptionalHint:
    "Needed for the linear speed and the centripetal acceleration.",
  stepOmega:
    "Angular speed ω = {omega} rad/s, which is {rpm} revolutions per minute (ω = 2πf, and 1 rev = 2π rad).",
  stepPeriod:
    "Period T = 2π ÷ ω = 2π ÷ {omega} = {period} s, and frequency f = 1 ÷ T = {frequency} Hz.",
  stepSpeed: "Speed v = ω × r = {omega} × {radius} = {speed} m/s.",
  stepAcceleration:
    "Centripetal acceleration a = ω² × r = {omega}² × {radius} = {acceleration} m/s².",
  copyText: "ω = {omega}; T = {period}; f = {frequency}",
  circularNeed: "Enter a value to convert.",
  circularUnreadable: "Enter the value and the radius as plain numbers.",
  circularErrors: {
    "not-positive": "The value must be greater than zero.",
    "radius-needed":
      "A speed needs a radius to give the angular speed, period and frequency. Enter a radius, or give ω, T or f instead.",
    radius: "The radius must be greater than zero.",
  },
  assumptions:
    "Assumes uniform circular motion: constant speed on a circle. Fc is the net inward force. The friction coefficient shown is the smallest static value that keeps a car on a flat, unbanked curve.",
} as const;

type Mode = "speed" | "angular" | "convert";
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const INITIAL_SPEED = initialRelationState<CentripetalSpeedKey>(
  CENTRIPETAL_SPEED,
  "F",
  {
    m: { value: "1200", unit: "kg" },
    v: { value: "72", unit: "kph" },
    r: { value: "100", unit: "m" },
  }
);
const INITIAL_ANGULAR = initialRelationState<CentripetalAngularKey>(
  CENTRIPETAL_ANGULAR,
  "F",
  {
    m: { value: "0.2", unit: "kg" },
    w: { value: "120", unit: "rpm" },
    r: { value: "0.5", unit: "m" },
  }
);
const INITIAL_CONVERT: CircularState = {
  given: "w",
  value: "3000",
  unit: "rpm",
  radius: "0.1",
  radiusUnit: "m",
};

function Extras({
  all,
  strings,
  fmt,
  sig,
}: {
  all: { m: number; r: number; F: number; speed: number };
  strings: typeof CENTRIPETAL_FORCE_CALCULATOR_STRINGS;
  fmt: Fmt;
  sig: number;
}) {
  const r = (value: number) => formatSig(fmt, value, sig);
  const acceleration = all.F / all.m;
  const omega = all.speed / all.r;
  const moving = omega > 0;
  const period = (2 * Math.PI) / omega;
  return (
    <>
      <p className="font-medium text-foreground text-sm">
        {strings.extrasHeading}
      </p>
      <StatGrid>
        <StatCard
          label={strings.acceleration}
          value={withUnit(r(acceleration), "m/s²")}
        />
        <StatCard
          label={strings.accelerationG}
          value={r(acceleration / STANDARD_GRAVITY)}
        />
        <StatCard label={strings.omega} value={withUnit(r(omega), "rad/s")} />
        {moving ? (
          <StatCard label={strings.period} value={withUnit(r(period), "s")} />
        ) : null}
        {moving ? (
          <StatCard
            label={strings.frequency}
            value={withUnit(r(1 / period), "Hz")}
          />
        ) : null}
        {moving ? (
          <StatCard
            label={strings.rpm}
            value={withUnit(r((60 * omega) / (2 * Math.PI)), "rpm")}
          />
        ) : null}
        <StatCard
          label={strings.muNeeded}
          value={r(acceleration / STANDARD_GRAVITY)}
        />
      </StatGrid>
      <p className={HINT}>{strings.assumptions}</p>
    </>
  );
}

export default function CentripetalForceCalculatorWidget() {
  const strings = useToolUi(CENTRIPETAL_FORCE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [mode, setMode] = useState<Mode>("speed");
  const [speed, setSpeed] = useState(INITIAL_SPEED);
  const [angular, setAngular] = useState(INITIAL_ANGULAR);
  const [convert, setConvert] = useState(INITIAL_CONVERT);
  const [sigFigs, setSigFigs] = useState("4");
  const sig = Number(sigFigs);
  const panel = {
    ...strings,
    errors: strings.errors as Record<string, string>,
  };
  const common = { fmt, sig, strings: panel };
  const speedLabels: Record<CentripetalSpeedKey, string> = {
    F: strings.force,
    m: strings.mass,
    v: strings.speed,
    r: strings.radius,
  };
  const angularLabels: Record<CentripetalAngularKey, string> = {
    F: strings.force,
    m: strings.mass,
    w: strings.angular,
    r: strings.radius,
  };
  const speedEval = evaluateRelation(CENTRIPETAL_SPEED, speed);
  const angularEval = evaluateRelation(CENTRIPETAL_ANGULAR, angular);
  const hints = {
    speed: strings.speedHint,
    angular: strings.angularHint,
    convert: strings.convertHint,
  };
  const circular = {
    ...strings,
    radius: strings.radiusOptional,
    radiusHint: strings.radiusOptionalHint,
    circularErrors: strings.circularErrors,
  };

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="cp-mode"
        onChange={setMode}
        options={[
          { value: "speed", label: strings.modeSpeed },
          { value: "angular", label: strings.modeAngular },
          { value: "convert", label: strings.modeConvert },
        ]}
        value={mode}
      />
      <p className={HINT}>{hints[mode]}</p>
      {mode === "speed" ? (
        <RelationInputs
          {...common}
          def={CENTRIPETAL_SPEED}
          evaluation={speedEval}
          hints={{ r: strings.radiusHint }}
          idPrefix="cp-speed"
          labels={speedLabels}
          onState={setSpeed}
          state={speed}
        />
      ) : null}
      {mode === "angular" ? (
        <RelationInputs
          {...common}
          def={CENTRIPETAL_ANGULAR}
          evaluation={angularEval}
          hints={{ r: strings.radiusHint }}
          idPrefix="cp-angular"
          labels={angularLabels}
          onState={setAngular}
          state={angular}
        />
      ) : null}
      {mode === "convert" ? (
        <CircularInputs
          onState={setConvert}
          state={convert}
          strings={circular}
        />
      ) : null}
      <div className="w-60">
        <SelectField
          id="cp-sig"
          label={strings.rounding}
          onChange={setSigFigs}
          options={SIG_FIG_CHOICES.map((choice) => ({
            value: choice,
            label: tPlural(strings.sigFigs, locale, Number(choice), {
              count: fmt(Number(choice)),
            }),
          }))}
          value={sigFigs}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "speed" ? (
          <RelationResults
            {...common}
            def={CENTRIPETAL_SPEED}
            evaluation={speedEval}
            extras={(all) => (
              <Extras
                all={{ m: all.m, r: all.r, F: all.F, speed: all.v }}
                fmt={fmt}
                sig={sig}
                strings={strings}
              />
            )}
            labels={speedLabels}
            state={speed}
          />
        ) : null}
        {mode === "angular" ? (
          <RelationResults
            {...common}
            def={CENTRIPETAL_ANGULAR}
            evaluation={angularEval}
            extras={(all) => (
              <Extras
                all={{ m: all.m, r: all.r, F: all.F, speed: all.w * all.r }}
                fmt={fmt}
                sig={sig}
                strings={strings}
              />
            )}
            labels={angularLabels}
            state={angular}
          />
        ) : null}
        {mode === "convert" ? (
          <CircularResults
            fmt={fmt}
            sig={sig}
            state={convert}
            strings={circular}
          />
        ) : null}
      </section>
    </>
  );
}
